import React from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import SvgIcon from '@mui/material/SvgIcon'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { AnimatePresence, motion } from 'framer-motion'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import LinkIcon from '@mui/icons-material/Link'
import { GlassButton, GlassOutlineButton, GlassPanel } from '@components/common/Glass'
import {
  altColor,
  altColorDarker,
  altColorDarkText,
  altColorLighter,
  helperTextColor,
  mainColor,
  mainColorDarker,
  mainColorDarkText,
  mainColorLighter,
  mainColorMediumText,
  shadow1,
  shadow2,
} from 'src/ui/theme/tokens'
import { MatchmakingStats } from 'src/api/types/matchmaking'
import { getMatchmakingLeaderboard } from 'src/api/matchmaking'
import { err } from 'src/utils/functions'
import LanguageIcon from '@mui/icons-material/Language'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import LeaderboardIcon from '@mui/icons-material/Leaderboard'
import Divider from '@mui/material/Divider'
import type { Player } from 'src/pages/Dashboard'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { getShareOrigin } from 'src/utils/helpers/share'

import TagRoundedIcon from '@mui/icons-material/TagRounded'
import Button from '@mui/material/Button'

type DashboardOnlineLobbyViewProps = {
  experience: 'lobby' | 'online'
  onlineUsersAll: number
  onlineUsersLogged: number
  searchingRanked: number
  searchingUnranked: number
  matchmakingRated: boolean
  onSetMatchmakingRated: (value: boolean) => void
  onlineMatchType?: 'matchmaking' | 'lobby'
  pendingMatch: boolean
  gameCode: string
  joinGameCode: string
  onlineProfileName: string
  onlineProfileIcon: string
  isAuthenticated: boolean
  canEditOnlineName: boolean
  leaderboard: MatchmakingStats[]
  myStats?: MatchmakingStats
  onlineMatchRated?: boolean
  players: Player[]
  hostId: string
  connectionId: string
  onChangeOnlineTeam: (team: number) => void
  onChangeOnlineIcon: (icon: string) => void
  onChangeOnlineName: (name: string) => void
  onCommitOnlineName: () => void
  onStartOnlineLobbyGame: () => void
  onChangeJoinCode: (value: string) => void
  handleMakeMatch: () => void
  handleCreateLobby: (profile?: { name?: string; icon?: string }) => void
  handleJoinLobby: (profile?: { name?: string; icon?: string }) => void
  onCloseLobby: () => void
  onLeaveLobby: () => void
  onUiAction?: () => void
}

const DashboardOnlineLobbyView: React.FC<DashboardOnlineLobbyViewProps> = ({
  experience,
  onlineUsersAll,
  onlineUsersLogged,
  searchingRanked,
  searchingUnranked,
  matchmakingRated,
  onSetMatchmakingRated,
  onlineMatchType,
  pendingMatch,
  gameCode,
  joinGameCode,
  onlineProfileName,
  onlineProfileIcon,
  isAuthenticated,
  canEditOnlineName,
  leaderboard,
  myStats,
  onlineMatchRated,
  players,
  hostId,
  connectionId,
  onChangeOnlineTeam,
  onChangeOnlineIcon,
  onChangeOnlineName,
  onCommitOnlineName,
  onStartOnlineLobbyGame,
  onChangeJoinCode,
  handleMakeMatch,
  handleCreateLobby,
  handleJoinLobby,
  onCloseLobby,
  onLeaveLobby,
  onUiAction,
}) => {
  const clickUi = () => onUiAction?.()
  const emojiOptions = ['😀', '😎', '🤖', '🔥', '⚡', '🧠', '🎯', '🚀']
  const [leaderboardOpen, setLeaderboardOpen] = React.useState(false)
  const [copiedCode, setCopiedCode] = React.useState(false)
  const [copiedShareLink, setCopiedShareLink] = React.useState(false)
  const [fullLeaderboard, setFullLeaderboard] = React.useState<MatchmakingStats[]>([])
  const [fullLeaderboardLoading, setFullLeaderboardLoading] = React.useState(false)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const panel = experience === 'lobby' ? 'lobby' : 'matchmaking'
  const shareOrigin = getShareOrigin()

  const shareUrl =
    gameCode && typeof window !== 'undefined'
      ? `${shareOrigin}/share/game/${encodeURIComponent(
          String(gameCode)
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
        )}`
      : ''

  const myPlayer = React.useMemo(
    () => players.find((player) => player.id === connectionId),
    [players, connectionId]
  )
  const isHost = !!gameCode && !!hostId && hostId === connectionId
  const teamOnePlayers = players.filter((player) => player.team === 1)
  const teamTwoPlayers = players.filter((player) => player.team === 2)
  const canStartLobbyGame =
    !!gameCode &&
    isHost &&
    teamOnePlayers.length > 0 &&
    teamTwoPlayers.length > 0 &&
    players.length >= 2

  const copyToClipboard = React.useCallback(async (value: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value)
        return true
      }
    } catch {
      // ignore
    }
    return false
  }, [])

  const showCopiedCode = () => {
    setCopiedCode(true)
    window.setTimeout(() => setCopiedCode(false), 1800)
  }

  const showCopiedShareLink = () => {
    setCopiedShareLink(true)
    window.setTimeout(() => setCopiedShareLink(false), 1800)
  }

  const loadFullLeaderboard = React.useCallback(() => {
    setFullLeaderboardLoading(true)
    getMatchmakingLeaderboard({ limit: 110, offset: 0 })
      .then((res) => {
        if (res.success && res.data) {
          setFullLeaderboard(res.data)
        }
      })
      .catch((e) => err(e))
      .finally(() => {
        setFullLeaderboardLoading(false)
      })
  }, [])

  React.useEffect(() => {
    if (!leaderboardOpen) return
    if (!fullLeaderboard.length && leaderboard.length) {
      setFullLeaderboard(leaderboard.slice(0, 110))
    }
    loadFullLeaderboard()
  }, [leaderboardOpen, loadFullLeaderboard, leaderboard, fullLeaderboard.length])

  const buildMatchmakingDisplayRows = React.useCallback(
    (rows: MatchmakingStats[], meStats?: MatchmakingStats) => {
      const useDummy = false
      const total = rows.length
      const meUserId = meStats?.userId

      const byUserId = new Map<number, MatchmakingStats>()
      const baseRows: MatchmakingStats[] = []
      rows.forEach((row) => {
        if (row.userId) {
          if (!byUserId.has(row.userId)) {
            byUserId.set(row.userId, row)
          }
          return
        }
        baseRows.push(row)
      })

      if (meUserId) {
        byUserId.set(
          meUserId,
          meStats || byUserId.get(meUserId) || ({} as MatchmakingStats)
        )
      }

      const mergedRows = baseRows.concat(Array.from(byUserId.values()))

      mergedRows.sort((a, b) => b.elo - a.elo)

      mergedRows.forEach((row, idx) => {
        row.rank = idx + 1
      })

      const inferredMeRank = meUserId
        ? mergedRows.findIndex((row) => row.userId === meUserId) + 1
        : meStats?.rank && meStats.rank > 0
          ? meStats.rank
          : Math.min(150, mergedRows.length)
      const meIndex = meUserId
        ? mergedRows.findIndex((row) => row.userId === meUserId)
        : mergedRows.findIndex((row) => row.rank === inferredMeRank)
      const safeMeIndex =
        meIndex >= 0 ? meIndex : Math.min(mergedRows.length - 1, inferredMeRank - 1)
      const aroundStart = Math.max(0, safeMeIndex - 50)
      const aroundEnd = Math.min(mergedRows.length, safeMeIndex + 51)

      const top = mergedRows.slice(0, Math.min(10, mergedRows.length))
      const around = mergedRows.slice(aroundStart, aroundEnd)

      const mergedRanks = new Set<number>()
      const display: Array<
        | { type: 'row'; row: MatchmakingStats; isMe?: boolean }
        | { type: 'divider'; key: string }
      > = []

      top.forEach((row) => {
        mergedRanks.add(row.rank)
        display.push({
          type: 'row',
          row,
          isMe: meUserId ? row.userId === meUserId : row.rank === inferredMeRank,
        })
      })

      if (
        around.length &&
        (around[0]?.rank || 0) > (top[top.length - 1]?.rank || 0) + 1
      ) {
        display.push({ type: 'divider', key: 'divider-top' })
      }

      around.forEach((row) => {
        if (mergedRanks.has(row.rank)) return
        mergedRanks.add(row.rank)
        display.push({
          type: 'row',
          row,
          isMe: meUserId ? row.userId === meUserId : row.rank === inferredMeRank,
        })
      })

      return { display, useDummy }
    },
    []
  )

  const matchmakingDisplay = React.useMemo(
    () => buildMatchmakingDisplayRows(fullLeaderboard, myStats),
    [buildMatchmakingDisplayRows, fullLeaderboard, myStats]
  )

  const myDerivedRank = React.useMemo(() => {
    if (!myStats) return undefined
    const rows = (fullLeaderboard.length ? fullLeaderboard : leaderboard).slice()
    if (!rows.length) return myStats.rank
    rows.sort((a, b) => b.elo - a.elo)
    const meUserId = myStats.userId
    const idx = meUserId
      ? rows.findIndex((row) => row.userId === meUserId)
      : rows.findIndex((row) => row.rank === myStats.rank)
    return idx >= 0 ? idx + 1 : myStats.rank
  }, [myStats, fullLeaderboard, leaderboard])

  const myMatchRowRef = React.useRef<HTMLTableRowElement | null>(null)
  const matchTableContainerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!leaderboardOpen) return
    let frame = 0
    let timeout: number | undefined
    const maxTries = 12
    const attempt = () => {
      const container = matchTableContainerRef.current
      const row = myMatchRowRef.current
      if (container && row) {
        const offset = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' })
        return
      }
      if (frame < maxTries) {
        frame += 1
        timeout = window.setTimeout(() => requestAnimationFrame(attempt), 80)
      }
    }
    timeout = window.setTimeout(() => requestAnimationFrame(attempt), 120)
    return () => {
      if (timeout) window.clearTimeout(timeout)
    }
  }, [leaderboardOpen, matchmakingDisplay.display.length])

  return (
    <Box sx={{ pb: 3, px: { xs: 1.5, sm: 3 }, pt: 1 }}>
      {panel === 'matchmaking' ? (
        <>
          <Box pt={0} pb={1}>
            {myStats ? (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                justifyContent={{
                  xs: 'space-between',
                  // sm: 'flex-start',
                }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  disabled={!matchmakingRated}
                  label={
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <LeaderboardIcon fontSize="small" sx={{ color: mainColorDarker }} />
                      <Typography variant="body1" fontWeight={700}>
                        Rank #{myDerivedRank ?? myStats.rank}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: { xs: '160px', sm: '170px' },
                    minWidth: '160px',
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    fontWeight: 700,
                    color: mainColorMediumText,
                    '& .MuiChip-label, & .MuiTypography-root': {
                      fontWeight: 700,
                    },
                  }}
                  clickable
                  onClick={() => {
                    clickUi()
                    setLeaderboardOpen(true)
                  }}
                />

                <Chip
                  size="small"
                  disabled={!matchmakingRated}
                  variant="outlined"
                  label={
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <EmojiEventsIcon fontSize="small" sx={{ color: altColor }} />
                      <Typography variant="body1" fontWeight={700}>
                        {myStats.elo} Trophies
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: { xs: '160px', sm: '170px' },
                    minWidth: '160px',
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    fontWeight: 700,
                    color: mainColorMediumText,
                    '& .MuiChip-label, & .MuiTypography-root': {
                      fontWeight: 700,
                    },
                  }}
                  clickable
                  onClick={() => {
                    clickUi()
                    setLeaderboardOpen(true)
                  }}
                />
              </Box>
            ) : (
              <Box height="24px"></Box>
            )}
          </Box>
        </>
      ) : (
        <Box height="24px" mt={1}></Box>
      )}
      <Box sx={{ p: 1, pt: 1 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
          >
            <Typography
              variant="h4"
              fontWeight={700}
              fontSize={{ xs: 28, sm: 36 }}
              sx={{
                width: '100%',
              }}
              textAlign="center"
            >
              {panel === 'matchmaking'
                ? 'A little friendly rivalry.'
                : 'Your game. Your people.'}
              <SvgIcon
                fontSize="large"
                sx={{
                  ml: '8px',
                  opacity: 0.33,
                  mb: '-7px',
                }}
              >
                {panel === 'matchmaking' ? <LanguageIcon /> : <DevicesRoundedIcon />}
              </SvgIcon>
            </Typography>
            {/* <Chip
              size="small"
              color={onlineMatchRated === false ? 'default' : 'info'}
              label={onlineMatchRated === false ? 'Unranked' : 'Rated'}
              sx={{ fontWeight: 600 }}
            /> */}
            <Typography
              variant="body1"
              sx={{
                color: helperTextColor,
              }}
            >
              {panel === 'matchmaking'
                ? `Ten ranks. Two players. Bring your best guesses.`
                : 'Share code to play cross-device'}
            </Typography>
          </Stack>

          <AnimatePresence mode="wait" initial={false}>
            {panel === 'matchmaking' ? (
              <motion.div
                key="matchmaking-view"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
              >
                <Box
                  className="rt-match-arena"
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    mb: 2.5,
                    borderRadius: '30px',
                    background: '#193b58',
                    color: '#ffffff',
                    border: '1px solid #335770',
                    boxShadow: '0 4px 0 #bfd2e3',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#a5d9ed',
                      letterSpacing: '.12em',
                      fontWeight: 700,
                    }}
                  >
                    {pendingMatch ? 'FINDING YOUR NEXT RIVAL' : 'HEAD-TO-HEAD TRIVIA'}
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={{ xs: 2.5, sm: 6 }}
                    mt={2.5}
                    mb={2}
                  >
                    <Box textAlign="center">
                      <Box
                        sx={{
                          width: 68,
                          height: 76,
                          mx: 'auto',
                          display: 'grid',
                          placeItems: 'center',
                          background: '#53d9f5',
                          borderRadius: '24px',
                          fontSize: 36,
                          transform: 'rotate(-5deg)',
                          boxShadow: '0 4px 0 #3787a8',
                        }}
                      >
                        {onlineProfileIcon || '🙂'}
                      </Box>
                      <Typography sx={{ color: '#b9d4e5', mt: 1, fontSize: 12 }}>
                        {onlineProfileName || 'You'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        color: '#193b58',
                        background: '#ffe178',
                        borderRadius: '14px',
                        p: 1,
                        fontWeight: 800,
                        transform: 'rotate(6deg)',
                      }}
                    >
                      VS
                    </Box>
                    <Box textAlign="center">
                      <Box
                        sx={{
                          width: 68,
                          height: 76,
                          mx: 'auto',
                          display: 'grid',
                          placeItems: 'center',
                          background: '#294e6b',
                          borderRadius: '24px',
                          fontSize: 40,
                          fontWeight: 800,
                          transform: 'rotate(5deg)',
                          boxShadow: '0 4px 0 #132b43',
                          color: '#7fadc8',
                        }}
                      >
                        ?
                      </Box>
                      <Typography sx={{ color: '#b9d4e5', mt: 1, fontSize: 12 }}>
                        Your next rival
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#b9d4e5' }}>
                    {pendingMatch
                      ? 'Hang tight. You can cancel your search anytime.'
                      : 'One great list. A proper showdown.'}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  gap={1}
                  flexDirection="column"
                  alignItems="center"
                  width="100%"
                  py={2}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    // mt={1}
                    // mb={1}
                    px={0}
                    sx={{
                      width: '100%',
                      maxWidth: '400px',
                    }}
                  >
                    <Box display="flex" gap={1} width="100%" alignItems="center">
                      <GlassButton
                        disabled={!isAuthenticated}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          background: matchmakingRated ? mainColor : mainColorLighter,
                          ...(!isAuthenticated
                            ? {
                                background: '#eee',
                                borderColor: '#eee',
                              }
                            : {}),
                          border: `2px solid ${matchmakingRated ? '#087fa2' : '#cfe2ec'}`,
                          boxShadow: 'none',
                          opacity: 1,
                          '&:hover': {
                            boxShadow: shadow2,
                            background: mainColor,
                            borderColor: matchmakingRated ? '#087fa2' : '#cfe2ec',
                            ...(!isAuthenticated
                              ? {
                                  background: '#eee',
                                  borderColor: '#eee',
                                }
                              : {}),
                          },
                        }}
                        onClick={() => {
                          if (!isAuthenticated) return
                          clickUi()
                          onSetMatchmakingRated(true)
                        }}
                      >
                        <Box px={2} py={0} display="flex" gap={1} alignItems="center">
                          <EmojiEventsIcon
                            fontSize="inherit"
                            sx={{
                              fontSize: '20px',
                              color: mainColorMediumText,
                              ...(!isAuthenticated
                                ? {
                                    color: 'gray',
                                  }
                                : {}),
                            }}
                          />
                          <Typography
                            variant="body2"
                            textAlign="center"
                            fontWeight={800}
                            sx={{
                              color: mainColorMediumText,
                              ...(!isAuthenticated
                                ? {
                                    color: 'gray',
                                  }
                                : {}),
                            }}
                          >
                            Ranked
                          </Typography>
                        </Box>
                      </GlassButton>
                      <GlassButton
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          background: !matchmakingRated ? altColor : altColorLighter,
                          borderColor: !matchmakingRated ? altColor : altColorLighter,
                          border: `2px solid ${!matchmakingRated ? '#c77b50' : '#efddc9'}`,
                          boxShadow: !matchmakingRated
                            ? '0 4px 0 #c77b50, 0 7px 14px rgba(148,66,23,0.12), inset 0 2px 0 #ffdfc2'
                            : '0 3px 0 #efddc9',
                          opacity: 1,
                          display: 'flex',
                          alignItems: 'center',
                          '&:hover': {
                            background: !matchmakingRated ? altColor : altColorLighter,
                            boxShadow: !matchmakingRated
                              ? '0 4px 0 #c77b50, 0 7px 14px rgba(148,66,23,0.12), inset 0 2px 0 #ffdfc2'
                              : '0 3px 0 #efddc9',
                          },
                        }}
                        onClick={() => {
                          clickUi()
                          onSetMatchmakingRated(false)
                        }}
                      >
                        <Box px={2} py={0} display="flex" gap={1} alignItems="center">
                          <Typography
                            variant="body2"
                            textAlign="center"
                            fontWeight={800}
                            sx={{
                              color: !matchmakingRated
                                ? altColorDarkText
                                : altColorDarkText,
                            }}
                          >
                            Unranked
                          </Typography>
                        </Box>
                      </GlassButton>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      clickUi()
                      handleMakeMatch()
                    }}
                    fullWidth
                    sx={{
                      maxWidth: '400px',
                      width: '100%',
                      minHeight: 52,
                      fontSize: 17,
                      fontWeight: 700,
                      ...(pendingMatch
                        ? {
                            background: 'white',
                            boxShadow: shadow2,
                            animation: 'searchPulseFloat 2000ms ease-in-out infinite',
                            '@keyframes searchPulseFloat': {
                              '0%': {
                                transform: 'translateY(0px) scale(1)',
                                boxShadow: `0 0 0 0 rgba(14,165,233,0.30), ${shadow1}`,
                              },
                              '50%': {
                                transform: 'translateY(0px) scale(1.009)',
                                boxShadow: `0 0 0 8px rgba(14,165,233,0), ${shadow2}`,
                              },
                              '100%': {
                                transform: 'translateY(0px) scale(1)',
                                boxShadow: `0 0 0 0 rgba(14,165,233,0), ${shadow1}`,
                              },
                            },
                          }
                        : {}),
                    }}
                    startIcon={!pendingMatch ? <SearchRoundedIcon /> : undefined}
                  >
                    {!pendingMatch ? (
                      'Find a Match'
                    ) : (
                      <Typography
                        fontWeight={800}
                        fontSize={20}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        Searching
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', minWidth: '1.2em' }}
                        >
                          {[0, 1, 2].map((dot) => (
                            <Box
                              key={`search-dot-${dot}`}
                              component="span"
                              sx={{
                                opacity: 0.2,
                                fontSize: '25px',
                                mb: 0.33,
                                animation: 'searchDots 1500ms infinite',
                                animationDelay: `${dot * 500}ms`,
                                '@keyframes searchDots': {
                                  '0%': { opacity: 0.4 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.4 },
                                },
                              }}
                            >
                              .
                            </Box>
                          ))}
                        </Box>
                      </Typography>
                    )}
                  </Button>

                  {/* {pendingMatch ? (
                  <Typography variant="caption" sx={{ color: helperTextColor }}>
                    Searching for opponent...
                  </Typography>
                ) : null} */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: mainColorDarker,
                      fontWeight: 500,
                    }}
                    width="100%"
                  >
                    {(matchmakingRated ? onlineUsersLogged : onlineUsersAll) > 1
                      ? `${matchmakingRated ? onlineUsersLogged - 1 : onlineUsersAll - 1} player${
                          (matchmakingRated
                            ? onlineUsersLogged - 1
                            : onlineUsersAll - 1) === 1
                            ? ''
                            : 's'
                        } online`
                      : 'No players online'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: helperTextColor }}>
                    {`All: ${onlineUsersAll} • Ranked: ${onlineUsersLogged} • Searching: ${
                      matchmakingRated ? searchingRanked : searchingUnranked
                    }`}
                  </Typography>
                  {!isAuthenticated && matchmakingRated ? (
                    <Typography variant="caption" sx={{ color: helperTextColor }}>
                      Log in to queue for ranked.
                    </Typography>
                  ) : null}
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="lobby-view"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
              >
                <Stack spacing={1}>
                  <Box py={2}>
                    {!gameCode ? (
                      <GlassOutlineButton
                        size="large"
                        variant="outlined"
                        onClick={() => {
                          clickUi()
                          handleCreateLobby({
                            icon: onlineProfileIcon,
                          })
                        }}
                        disabled={pendingMatch || !!gameCode}
                        startIcon={<DevicesRoundedIcon />}
                        fullWidth
                        sx={{
                          background: 'white',
                          maxWidth: '400px',
                          height: '56px',
                          px: 3,
                          color: mainColorDarker,
                          boxShadow: shadow2,
                          borderWidth: '2px',
                          '&: hover': {
                            borderWidth: '2px',
                            borderColor: mainColor,
                            transform: 'translateY(-3px) scale(1.01)',
                            filter: 'brightness(1.04)',
                            boxShadow: shadow2,
                          },
                          transition:
                            'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
                          animation: 'matchBtnFloat 2800ms ease-in-out infinite',
                          '@keyframes matchBtnFloat': {
                            '0%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-1px)' },
                            '100%': { transform: 'translateY(0px)' },
                          },
                        }}
                      >
                        <Typography fontWeight={700} fontSize={20}>
                          Create Lobby
                        </Typography>
                      </GlassOutlineButton>
                    ) : (
                      <GlassPanel
                        sx={{
                          p: 1.25,
                          py: 2,
                          backgroundColor: mainColorLighter,
                          borderColor: mainColorLighter,
                          borderWidth: '2px',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          gap={2}
                        >
                          <Box flexGrow={1} px={1}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              GAME CODE
                            </Typography>
                            <Typography
                              variant="h4"
                              fontWeight={600}
                              sx={{ letterSpacing: '0.12em', lineHeight: 1.1 }}
                            >
                              {gameCode}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Tooltip
                              title={copiedCode ? 'Copied to clipboard' : 'Copy code'}
                              arrow
                            >
                              <Box>
                                <GlassButton
                                  variant="outlined"
                                  size="small"
                                  onClick={async () => {
                                    clickUi()
                                    if (!gameCode) return
                                    const copied = await copyToClipboard(gameCode)
                                    if (copied) {
                                      showCopiedCode()
                                    }
                                  }}
                                  sx={{ color: 'inherit', minWidth: 110 }}
                                >
                                  <Box display="flex" alignItems="center" gap={0.75}>
                                    <TagRoundedIcon fontSize="small" color="disabled" />
                                    <Typography variant="body2" fontWeight={700}>
                                      Copy Code
                                    </Typography>
                                  </Box>
                                </GlassButton>
                              </Box>
                            </Tooltip>
                            <Tooltip
                              title={
                                copiedShareLink ? 'Copied to clipboard' : 'Share lobby'
                              }
                              arrow
                            >
                              <Box>
                                <GlassButton
                                  variant="outlined"
                                  size="small"
                                  onClick={async () => {
                                    clickUi()
                                    if (!shareUrl) return
                                    try {
                                      if (Capacitor.isNativePlatform()) {
                                        await Share.share({
                                          title: `Join my RankTrivia game lobby #${gameCode}`,
                                          url: shareUrl,
                                          dialogTitle: 'Share',
                                        })
                                        return
                                      }
                                      if (navigator.share) {
                                        await navigator.share({
                                          title: `Join my RankTrivia game lobby #${gameCode}`,
                                          url: shareUrl,
                                        })
                                        return
                                      }
                                    } catch {
                                      // ignore and fallback to clipboard
                                    }
                                    const copied = await copyToClipboard(shareUrl)
                                    if (copied) {
                                      showCopiedShareLink()
                                    }
                                  }}
                                  sx={{ color: 'inherit', minWidth: 110 }}
                                >
                                  <Box display="flex" alignItems="center" gap={0.75}>
                                    <OpenInNewRoundedIcon
                                      fontSize="small"
                                      color="disabled"
                                    />
                                    <Typography variant="body2" fontWeight={700}>
                                      Share
                                    </Typography>
                                  </Box>
                                </GlassButton>
                              </Box>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </GlassPanel>
                    )}
                  </Box>
                  {!gameCode ? (
                    <Stack>
                      <Box mb={2.25}>
                        <Divider>
                          <Typography
                            sx={{
                              color: '#ccc',
                            }}
                          >
                            or
                          </Typography>
                        </Divider>
                      </Box>

                      <Box display="flex" gap={1} alignItems="center">
                        <Box flexGrow={1}>
                          <TextField
                            value={joinGameCode}
                            size="small"
                            fullWidth
                            placeholder="Join Lobby"
                            inputProps={{
                              maxLength: 5,
                            }}
                            onChange={(event) => onChangeJoinCode(event.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                background: '#f7fbfe',
                                borderRadius: '22px',
                                backdropFilter: 'none',
                                '& fieldset': { borderColor: '#d6e4ef' },
                                '&:hover fieldset': { borderColor: '#087fa2' },
                              },
                              '& .MuiInputBase-input': {
                                py: 1.25,
                              },
                            }}
                          />
                        </Box>
                        <GlassButton
                          variant="outlined"
                          onClick={() => {
                            clickUi()
                            handleJoinLobby({
                              icon: onlineProfileIcon,
                            })
                          }}
                          disabled={!joinGameCode || pendingMatch}
                          startIcon={<LinkIcon />}
                          sx={{
                            color: 'inherit',
                            fontWeight: 700,
                            background: !joinGameCode ? '#eee' : undefined,
                            boxShadow: !joinGameCode ? 'none' : undefined,
                          }}
                        >
                          Join
                        </GlassButton>
                      </Box>
                    </Stack>
                  ) : null}
                </Stack>

                {gameCode ? (
                  <Stack spacing={1.5} mt={1}>
                    <Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <GlassPanel
                            sx={{
                              p: 1.25,
                              backgroundColor: mainColorLighter,
                              borderColor: mainColorLighter,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography
                                variant="h6"
                                fontWeight={700}
                                color={mainColorDarker}
                              >
                                Blue Team
                              </Typography>
                              {myPlayer?.team !== 1 ? (
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    cursor: 'pointer',
                                    color: mainColorDarker,
                                    opacity: 0.85,
                                    '@media (hover: hover) and (pointer: fine)': {
                                      '&:hover': {
                                        opacity: 1,
                                        textDecoration: 'underline',
                                      },
                                    },
                                  }}
                                  onClick={() => {
                                    clickUi()
                                    onChangeOnlineTeam(1)
                                  }}
                                >
                                  Join
                                </Typography>
                              ) : null}
                            </Box>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              alignItems="center"
                              useFlexGap
                              p={1}
                              sx={{
                                minHeight: '48px',
                              }}
                            >
                              {teamOnePlayers?.length ? (
                                teamOnePlayers.map((player) => (
                                  <Box
                                    key={`team-1-${player.id || player.name}`}
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={0.75}
                                    sx={{ px: 0.5, py: 0.25 }}
                                  >
                                    <Typography component="span" aria-hidden>
                                      {player.icon || '🙂'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700}>
                                      {player.name}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" fontWeight={700}>
                                  No Players
                                </Typography>
                              )}
                            </Stack>
                          </GlassPanel>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <GlassPanel
                            sx={{
                              p: 1.25,
                              backgroundColor: altColorLighter,
                              borderColor: altColorLighter,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography
                                variant="h6"
                                fontWeight={700}
                                color={altColorDarker}
                              >
                                Orange Team
                              </Typography>
                              {myPlayer?.team !== 2 ? (
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    cursor: 'pointer',
                                    color: altColorDarker,
                                    opacity: 0.85,
                                    '@media (hover: hover) and (pointer: fine)': {
                                      '&:hover': {
                                        opacity: 1,
                                        textDecoration: 'underline',
                                      },
                                    },
                                  }}
                                  onClick={() => {
                                    clickUi()
                                    onChangeOnlineTeam(2)
                                  }}
                                >
                                  Join
                                </Typography>
                              ) : null}
                            </Box>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              p={1}
                              flexWrap="wrap"
                              useFlexGap
                              alignItems="center"
                              sx={{
                                minHeight: '48px',
                              }}
                            >
                              {teamTwoPlayers?.length ? (
                                teamTwoPlayers.map((player) => (
                                  <Box
                                    key={`team-2-${player.id || player.name}`}
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={0.75}
                                    sx={{ px: 0.5, py: 0.25 }}
                                  >
                                    <Typography component="span" aria-hidden>
                                      {player.icon || '🙂'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700}>
                                      {player.name}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" fontWeight={700}>
                                  No Players
                                </Typography>
                              )}
                            </Stack>
                          </GlassPanel>
                        </Grid>
                      </Grid>
                    </Box>

                    <GlassPanel sx={{ p: 1.25 }}>
                      {canEditOnlineName ? (
                        <Stack direction="row" spacing={1} mb={1.25}>
                          <TextField
                            value={onlineProfileName}
                            size="small"
                            fullWidth
                            placeholder="Display name"
                            inputProps={{ maxLength: 24 }}
                            onChange={(event) => onChangeOnlineName(event.target.value)}
                            onBlur={onCommitOnlineName}
                          />
                        </Stack>
                      ) : null}
                      <Stack gap={1}>
                        <Typography
                          textAlign="left"
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          AVATAR
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {emojiOptions.map((icon) => (
                            <Chip
                              key={`emoji-${icon}`}
                              clickable
                              color={
                                (myPlayer?.icon || onlineProfileIcon) === icon
                                  ? 'info'
                                  : 'default'
                              }
                              label={icon}
                              onClick={() => {
                                clickUi()
                                onChangeOnlineIcon(icon)
                              }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </GlassPanel>

                    {isHost ? (
                      <GlassButton
                        variant="contained"
                        onClick={() => {
                          clickUi()
                          onStartOnlineLobbyGame()
                        }}
                        disabled={!canStartLobbyGame}
                        sx={{
                          height: '56px',
                          px: 3,
                          backgroundColor: mainColor,
                        }}
                      >
                        <Typography fontWeight={700} fontSize={20}>
                          Start Game
                        </Typography>
                      </GlassButton>
                    ) : (
                      <Typography variant="body2" color={helperTextColor}>
                        Waiting for host to start the game.
                      </Typography>
                    )}
                    {!canStartLobbyGame && isHost ? (
                      <Typography variant="caption" color={helperTextColor}>
                        Need at least one player on each team to start.
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={3}
                >
                  <Box />
                  {gameCode ? (
                    <Chip
                      clickable
                      variant="filled"
                      // color={isHost ? 'warning' : 'default'}
                      label={isHost ? 'Close Lobby' : 'Leave Lobby'}
                      icon={
                        isHost ? (
                          <ExitToAppIcon color="disabled" />
                        ) : (
                          <LogoutRoundedIcon color="disabled" />
                        )
                      }
                      onClick={() => {
                        clickUi()
                        if (isHost) onCloseLobby()
                        else onLeaveLobby()
                      }}
                      sx={{
                        fontWeight: 700,
                        px: 1,
                        color: isHost ? 'white' : '#666',
                        backgroundColor: isHost ? altColor : 'default',
                      }}
                    />
                  ) : null}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </Box>

      <Dialog
        open={leaderboardOpen}
        onClose={() => {
          clickUi()
          setLeaderboardOpen(false)
        }}
        fullScreen={isSmallScreen}
        fullWidth
        maxWidth="md"
        sx={{
          backdropFilter: 'none',
          '& .MuiDialog-paper': {
            borderRadius: '30px',
            py: 1,
            background: '#e1f5fb',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 1, sm: 1, md: 3 },
            py: 2,
            pt: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
          }}
        >
          <Typography
            fontWeight={600}
            fontSize={{ xs: 26, sm: 30 }}
            sx={{
              width: '100%',
            }}
            textAlign="center"
            component="div"
          >
            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              <SvgIcon
                fontSize="large"
                sx={{
                  opacity: 0.33,
                }}
              >
                <LanguageIcon />
              </SvgIcon>
              Leaderboard
            </Box>
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            py: 2,
            px: {
              xs: 0.5,
              sm: 2,
            },
          }}
        >
          <Stack spacing={4}>
            {myStats ? (
              <Box
                display="flex"
                gap={1}
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  variant="outlined"
                  disabled={!matchmakingRated}
                  label={
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <LeaderboardIcon fontSize="small" sx={{ color: mainColorDarker }} />
                      <Typography variant="body1" fontWeight={700}>
                        Rank #{myDerivedRank ?? myStats.rank}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: { xs: '160px', sm: '170px' },
                    minWidth: '160px',
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    fontWeight: 700,
                    color: mainColorMediumText,
                    '& .MuiChip-label, & .MuiTypography-root': {
                      fontWeight: 700,
                    },
                  }}
                  clickable
                />

                <Chip
                  size="small"
                  disabled={!matchmakingRated}
                  variant="outlined"
                  label={
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <EmojiEventsIcon fontSize="small" sx={{ color: altColor }} />
                      <Typography variant="body1" fontWeight={700}>
                        {myStats.elo} Trophies
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: { xs: '160px', sm: '170px' },
                    minWidth: '160px',
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    fontWeight: 700,
                    color: mainColorMediumText,
                    '& .MuiChip-label, & .MuiTypography-root': {
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            ) : null}
            {fullLeaderboardLoading && !fullLeaderboard.length ? (
              <Typography variant="body2" color="text.secondary">
                Loading leaderboard...
              </Typography>
            ) : matchmakingDisplay.display.length ? (
              <TableContainer
                ref={matchTableContainerRef}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid rgba(226,232,240,0.9)',
                  background: '#ffffff',
                  maxHeight: { xs: '60vh', sm: '70vh' },
                  overflowY: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          py: 0.5,
                          pl: 1,
                          pr: 0,
                          width: 50,
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1 }}>
                        Player
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 0.5, px: 1 }}
                        align="right"
                      ></TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1 }} align="right">
                        Win %
                      </TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }} align="right">
                        W-L-T
                      </TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matchmakingDisplay.display.map((entry, idx) =>
                      entry.type === 'divider' ? (
                        <TableRow key={entry.key + idx}>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ color: helperTextColor }}
                          >
                            ...
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`full-lb-${entry.row.rank}-${entry.row.userId}`}
                          ref={entry.isMe ? myMatchRowRef : undefined}
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: 'rgba(248,250,252,0.55)',
                            },
                            ...(entry.isMe ? { backgroundColor: altColorLighter } : {}),
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: entry.row.rank < 10 ? 600 : 500,
                              py: 0.5,
                              px: 1,
                            }}
                          >
                            #{entry.row.rank}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: entry.row.rank < 10 ? 600 : 500,
                              py: 0.5,
                              px: 1,
                            }}
                          >
                            {entry.row.username.slice(0, 20)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                            {entry.row.elo}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                            {Math.round(entry.row.winRate)}%
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No ranked matchmaking records yet.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 4, sm: 3, md: 3 },
            py: { xs: 2, md: 3 },
            bgcolor: 'transparent',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
            px={1}
          >
            <Button
              variant="contained"
              onClick={() => {
                clickUi()
                setLeaderboardOpen(false)
              }}
              sx={{
                borderRadius: '999px',
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                background: '#e1f5fb',
                boxShadow: shadow2,
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DashboardOnlineLobbyView
